import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, InputNumber, Switch, Tabs } from "antd";
import { useCreate, useUpdate } from "../../../../hooks";
import { masterDataService } from "../../../../api/enhancedApi";
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import CurfewRulesEditor from "./CurfewRulesEditor";
import { getTimezoneOffset } from "../../../../utils/timezoneUtils";
import { AVIATION_TIMEZONES } from "../../../../utils/timezoneData";

/**
 * 机场表单组件
 * @param {Object} props
 * @param {boolean} props.visible - 是否显示表单对话框
 * @param {string} props.mode - 表单模式，'add'或'edit'
 * @param {Object} props.initialValues - 初始值，编辑模式下必传
 * @param {Function} props.onCancel - 取消回调函数
 * @param {Function} props.onSuccess - 提交成功回调函数
 */
const AirportForm = ({ visible, mode, initialValues, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState("1");
    const [depCurfewRules, setDepCurfewRules] = useState([]);
    const [arrCurfewRules, setArrCurfewRules] = useState([]);
    const [aircraftOptions, setAircraftOptions] = useState([]);
    const [loadingAircraft, setLoadingAircraft] = useState(false);

    // 创建机场的Hook
    const createMutation = useCreate("airports", masterDataService.airport.createAirport, {
        onSuccess: () => {
            onSuccess();
            form.resetFields();
            setDepCurfewRules([]);
            setArrCurfewRules([]);
        },
    });

    // 更新机场的Hook
    const updateMutation = useUpdate("airports", masterDataService.airport.updateAirport, {
        onSuccess: () => {
            onSuccess();
        },
    });

    // 加载飞机列表
    useEffect(() => {
        const loadAircraft = async () => {
            try {
                setLoadingAircraft(true);
                const response = await masterDataService.aircraft.getAircraftList({
                    limit: 100, // 修正：后端限制 limit 最大为 100
                    status: "ACTIVE", // 修正：使用 status 参数过滤，并假设值为 "ACTIVE"
                });

                // 后端 /api/v1/aircraft 返回 PaginatedResponse 结构
                const aircraftList = response?.data?.items || [];

                // 转换为选择器需要的格式
                const options = aircraftList.map((aircraft) => ({
                    value: aircraft.registration,
                    label: `${aircraft.registration} (${aircraft.aircraft_type || "未知机型"})`,
                }));

                setAircraftOptions(options);
            } catch (error) {
                console.error("加载飞机列表失败:", error);
            } finally {
                setLoadingAircraft(false);
            }
        };

        if (visible) {
            loadAircraft();
        }
    }, [visible]);

    // 当initialValues变化时重置表单
    useEffect(() => {
        if (visible && initialValues) {
            // 处理时区值
            let timezoneValue = initialValues.timezone;

            form.setFieldsValue({
                iata_code: initialValues.iata_code,
                icao_code: initialValues.icao_code,
                airport_name: initialValues.airport_name,
                airport_name_en: initialValues.airport_name_en,
                city: initialValues.city,
                country: initialValues.country,
                requires_slot_permit: initialValues.requires_slot_permit,
                timezone: timezoneValue,
                is_active: initialValues.is_active,
                latitude: initialValues.latitude,
                longitude: initialValues.longitude,
                turnaround_time: initialValues.turnaround_time,
            });

            // 设置宵禁规则
            setDepCurfewRules(initialValues.dep_curfew_rules || []);
            setArrCurfewRules(initialValues.arr_curfew_rules || []);
        } else if (visible) {
            form.resetFields();
            // 设置默认值
            form.setFieldsValue({
                country: "中国",
                timezone: "Asia/Shanghai",
                is_active: true,
                requires_slot_permit: false,
            });

            // 清空宵禁规则
            setDepCurfewRules([]);
            setArrCurfewRules([]);
        }
    }, [visible, initialValues, form]);

    // 表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // 处理时区值，确保它是正确的字符串格式
            if (values.timezone && typeof values.timezone === "object") {
                values.timezone = values.timezone.value;
            }

            // 添加宵禁规则
            values.dep_curfew_rules = depCurfewRules;
            values.arr_curfew_rules = arrCurfewRules;

            if (mode === "add") {
                createMutation.mutate(values);
            } else {
                updateMutation.mutate({
                    id: initialValues.iata_code,
                    data: values,
                });
            }
        } catch (error) {
            console.error("表单验证错误:", error);
        }
    };

    // 表单标题
    const title = mode === "add" ? "添加机场" : "编辑机场";

    // 是否处于提交中状态
    const loading = createMutation.isLoading || updateMutation.isLoading;

    // 时区选择器的自定义样式
    const timezoneLabelStyle = {
        color: "rgba(0, 0, 0, 0.88)",
        fontSize: "14px",
        marginBottom: "8px",
        display: "block",
    };

    // 自定义时区标签渲染
    const formatTimezoneLabel = (tz) => {
        // 尝试从initialValues中获取已知的偏移量
        if (initialValues && mode === "edit") {
            // 对于编辑模式，我们需要检查当前选中的时区是否与原时区相同
            const originalTimezone = initialValues.timezone;

            if (typeof tz === "string" && tz === originalTimezone && (initialValues.utc_offset_string || initialValues.utc_offset_minutes !== undefined)) {
                // 使用后端提供的偏移量
                if (initialValues.utc_offset_string) {
                    const offsetText = initialValues.utc_offset_string === "Z" ? "UTC+0" : `UTC${initialValues.utc_offset_string}`;
                    return `${tz} (${offsetText})`;
                } else if (typeof initialValues.utc_offset_minutes === "number") {
                    const hours = Math.floor(Math.abs(initialValues.utc_offset_minutes) / 60);
                    const minutes = Math.abs(initialValues.utc_offset_minutes) % 60;
                    const sign = initialValues.utc_offset_minutes >= 0 ? "+" : "-";
                    const minuteStr = minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : "";
                    const offsetText = `UTC${sign}${hours}${minuteStr}`;
                    return `${tz} (${offsetText})`;
                }
            } else if (typeof tz === "object" && tz.value === originalTimezone && (initialValues.utc_offset_string || initialValues.utc_offset_minutes !== undefined)) {
                // 使用后端提供的偏移量
                if (initialValues.utc_offset_string) {
                    const offsetText = initialValues.utc_offset_string === "Z" ? "UTC+0" : `UTC${initialValues.utc_offset_string}`;
                    return `${tz.value} (${offsetText})`;
                } else if (typeof initialValues.utc_offset_minutes === "number") {
                    const hours = Math.floor(Math.abs(initialValues.utc_offset_minutes) / 60);
                    const minutes = Math.abs(initialValues.utc_offset_minutes) % 60;
                    const sign = initialValues.utc_offset_minutes >= 0 ? "+" : "-";
                    const minuteStr = minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : "";
                    const offsetText = `UTC${sign}${hours}${minuteStr}`;
                    return `${tz.value} (${offsetText})`;
                }
            }
        }

        // 默认使用前端计算的偏移量
        if (typeof tz === "string") {
            return `${tz} (${getTimezoneOffset(tz)})`;
        } else if (tz && tz.value) {
            return `${tz.value} (${getTimezoneOffset(tz.value)})`;
        }
        return tz;
    };

    // 创建一个包含所有时区和我们自定义时区的新对象
    // 合并 react-timezone-select 的默认时区和我们的航空时区数据
    const customTimezones = {
        ...allTimezones,
        ...AVIATION_TIMEZONES,
    };

    // 标签页配置
    const tabItems = [
        {
            key: "1",
            label: "基本信息",
            children: (
                <Form form={form} layout="horizontal" labelAlign="left">
                    <div className="form-inline-group">
                        <Form.Item
                            name="iata_code"
                            label="IATA代码"
                            rules={[
                                { required: true, message: "请输入IATA代码" },
                                { pattern: /^[A-Z]{3}$/, message: "IATA代码必须为3个大写字母" },
                            ]}
                            tooltip="机场的IATA三字代码，如PEK"
                            disabled={mode === "edit"}
                        >
                            <Input
                                placeholder="请输入IATA代码"
                                maxLength={3}
                                disabled={mode === "edit"}
                                rules={[
                                    { required: true, message: "请输入IATA代码" },
                                    { pattern: /^[A-Z]{3}$/, message: "IATA代码必须为3个大写字母" },
                                ]}
                            />
                        </Form.Item>

                        <Form.Item name="icao_code" label="ICAO代码" rules={[{ pattern: /^[A-Z]{4}$/, message: "ICAO代码必须为4个大写字母" }]} tooltip="机场的ICAO四字代码，如ZBAA">
                            <Input placeholder="请输入ICAO代码" maxLength={4} />
                        </Form.Item>

                        <Form.Item name="airport_name" label="机场名称" rules={[{ required: true, message: "请输入机场名称" }]}>
                            <Input placeholder="请输入机场名称" />
                        </Form.Item>

                        <Form.Item name="airport_name_en" label="英文名称">
                            <Input placeholder="请输入英文名称" />
                        </Form.Item>

                        <Form.Item name="city" label="所在城市">
                            <Input placeholder="请输入所在城市" />
                        </Form.Item>

                        <Form.Item name="country" label="所在国家/地区" rules={[{ required: true, message: "请输入所在国家/地区" }]}>
                            <Input placeholder="请输入所在国家/地区" />
                        </Form.Item>

                        <Form.Item name="timezone" label="时区" rules={[{ required: true, message: "请选择时区" }]} className="timezone-form-item">
                            <TimezoneSelect
                                value={form.getFieldValue("timezone")}
                                onChange={(tz) => form.setFieldValue("timezone", tz)}
                                timezones={customTimezones} // 使用我们完整的时区数据
                                labelStyle={timezoneLabelStyle}
                                placeholder="请选择时区"
                                formatOptionLabel={formatTimezoneLabel}
                            />
                        </Form.Item>

                        <Form.Item name="latitude" label="纬度" rules={[{ type: "number", min: -90, max: 90, message: "纬度范围为-90到90" }]}>
                            <InputNumber placeholder="请输入纬度" style={{ width: "100%" }} step={0.000001} precision={6} />
                        </Form.Item>

                        <Form.Item name="longitude" label="经度" rules={[{ type: "number", min: -180, max: 180, message: "经度范围为-180到180" }]}>
                            <InputNumber placeholder="请输入经度" style={{ width: "100%" }} step={0.000001} precision={6} />
                        </Form.Item>

                        <Form.Item name="turnaround_time" label="最小过站时间" tooltip="飞机在该机场的最小过站时间(分钟)，将以小时:分钟格式显示" rules={[{ type: "number", min: 0, message: "过站时间不能为负数" }]}>
                            <InputNumber placeholder="请输入最小过站时间" style={{ width: "100%" }} min={0} step={5} formatter={(value) => (value ? `${value} 分钟` : "")} parser={(value) => value.replace(/[^0-9]/g, "")} />
                        </Form.Item>
                    </div>

                    <Form.Item name="is_active" label="状态" valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="停用" />
                    </Form.Item>

                    <Form.Item name="requires_slot_permit" label="时刻协调许可" valuePropName="checked" tooltip="启用后，该机场起降航班会自动推导 SLOT Permit 需求">
                        <Switch checkedChildren="需要" unCheckedChildren="不需要" />
                    </Form.Item>
                </Form>
            ),
        },
        {
            key: "2",
            label: "宵禁规则",
            children: <CurfewRulesEditor depCurfewRules={depCurfewRules} onDepCurfewRulesChange={setDepCurfewRules} arrCurfewRules={arrCurfewRules} onArrCurfewRulesChange={setArrCurfewRules} aircraftOptions={aircraftOptions} loadingAircraft={loadingAircraft} />,
        },
    ];

    return (
        <Modal title={title} open={visible} onCancel={onCancel} onOk={handleSubmit} confirmLoading={loading} maskClosable={false} destroyOnHidden width={800}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
        </Modal>
    );
};

export default AirportForm;
